import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { getFeatureEntityLog } from "services/feature-apis";
import { QUERY_SUBMITTAL_TYPES } from "services/graphQL/queries";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { useProjectParticipants } from "hooks/project-participants";
import { ProjectContext } from "context/ProjectProvider";
import { getGQL, isProjectInIntegrationMode } from "utils/utils";
import { PROJECT_INTG_CONFIGS } from "services/graphQL/ciq-queries";
import { FeatureTypes } from "entity-app/constants";
import { DELETE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { gql } from "@apollo/client";
import { TAuditChangeLog } from "change-events/change-event-polling";
import { getMaxNumberOfMilestonesFromEntityList } from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import {
  MAX_NUMBER_OF_FETCH_ITEM,
  SubmittalStatusToStrMap
} from "../../../constants";

function useFeatureSubmittalLogData(projectId: string, projectToken: string) {
  const { projectDetails, gqlClientForProject, eventLogs } =
    useContext(ProjectContext);

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const [submittalList, setSubmittalList] = useState<any>(null);
  const [submittalListLoading, setSubmittalListLoading] = useState(false);
  const [submittalListLoadedOnce, setSubmittalListLoadedOnce] = useState(false);

  const [onDataUpdateCallback, setOnDataUpdateCallback] = useState<any>(null);

  const { data: submittalTypes } = useCIQQuery(QUERY_SUBMITTAL_TYPES);

  const [mutationDeleteSubmittal] = useCIQMutation(
    gql(DELETE_FEATURE_INSTANCE),
    {
      client: gqlClientForProject
    }
  );

  const submittalTypesMap = useMemo(() => {
    if (submittalTypes) {
      const tmpTypesMap: any = {};
      submittalTypes.configurations_value.forEach((type: any) => {
        tmpTypesMap[type.value] = type;
      });
      return tmpTypesMap;
    }
    return null;
  }, [submittalTypes]);

  const statusesMap = useMemo(() => {
    const tmpStatusesMap: any = {};
    Object.keys(SubmittalStatusToStrMap).forEach((keyStatusId: any) => {
      tmpStatusesMap[keyStatusId] = {
        name: SubmittalStatusToStrMap[keyStatusId]
      };
    });
    return tmpStatusesMap;
  }, []);

  const { projectParticipants: WFProjectParticipants } =
    useProjectParticipants(isIntegrationMode);

  const { query: qProjectIntgConfigs } = useMemo(() => {
    return getGQL(PROJECT_INTG_CONFIGS);
  }, []);

  const { data: projectIntegConfig } = useCIQQuery(qProjectIntgConfigs, {
    client: gqlClientForProject,
    skip: !isIntegrationMode
  });

  const isIntegrationConfigured = !!(
    projectIntegConfig && projectIntegConfig?.project_integration_configs.length
  );

  const fetchLogData = useCallback(async () => {
    setSubmittalListLoading(true);
    const res = await getFeatureEntityLog(
      projectToken,
      FeatureTypes.SUBMITTALS
    );

    if (res.success) {
      setSubmittalList([...res.data]);
      setSubmittalListLoadedOnce(true);
    }
    setSubmittalListLoading(false);
  }, [projectToken]);

  useEffect(() => {
    if (projectId && projectToken) {
      fetchLogData();
    }
    return () => {};
  }, [fetchLogData, projectId, projectToken]);

  const maxNumberOfMilestones = useMemo(() => {
    return getMaxNumberOfMilestonesFromEntityList(submittalList || null);
  }, [submittalList]);

  const getAndUpdateEntityList = useCallback(
    async (featureInstanceIds: Array<string>) => {
      if (!submittalList) return;

      if (featureInstanceIds.length >= MAX_NUMBER_OF_FETCH_ITEM) {
        fetchLogData();
        return;
      }

      const res = await getFeatureEntityLog(
        projectToken,
        FeatureTypes.SUBMITTALS,
        featureInstanceIds
      );

      if (res?.success && res.data?.length) {
        const updatedList = res.data;

        const items = [...submittalList] as Array<any>;
        updatedList.forEach((item: any) => {
          const foundIndex = items.findIndex((x) => x.id === item.id);
          if (foundIndex === -1) {
            items.push(item);
          } else {
            items[foundIndex] = item;
          }
        });
        setSubmittalList(items);
      }
    },
    [submittalList, projectToken, fetchLogData]
  );

  const deleteEntitiesFromList = useCallback(
    (ids: Array<string>) => {
      const items = submittalList ? [...submittalList] : [];
      ids.forEach((id) => {
        const index = items.findIndex((x) => x.id === id);
        items.splice(index, 1);
      });
      setSubmittalList(items);
    },
    [submittalList]
  );

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs?.length && previousEventLogs.current !== eventLogs) {
      const featureLogs = eventLogs.filter(
        (log) =>
          (log.info?.feature_type === "SUBMITTALS" ||
            log.info?.feature_type_ids?.includes(FeatureTypes.SUBMITTALS)) &&
          log.data_source === "feature_instance"
      );

      if (featureLogs.length) {
        const deletedIds = featureLogs
          .filter((log) => ["delete", "bulk_delete"].includes(log.change_type))
          .map((s: TAuditChangeLog) => s.info.feature_instance_ids) as any;
        if (deletedIds.length) {
          const deletedDArray = [...new Set([].concat([], ...deletedIds))];
          deleteEntitiesFromList(deletedDArray);
        }

        const ids = featureLogs
          .filter((log) => !["delete", "bulk_delete"].includes(log.change_type)) // update create import
          .map((s) => s.info.feature_instance_ids) as any;
        if (ids.length) {
          const singleDArray = [...new Set([].concat([], ...ids))];
          getAndUpdateEntityList(singleDArray);
        }
        if (onDataUpdateCallback) {
          onDataUpdateCallback();
        }
      }
    }
    previousEventLogs.current = eventLogs;
  }, [
    deleteEntitiesFromList,
    eventLogs,
    getAndUpdateEntityList,
    onDataUpdateCallback
  ]);

  return {
    submittalList,
    maxNumberOfMilestones,
    submittalTypesMap,
    statusesMap,
    WFProjectParticipants,
    isIntegrationConfigured,
    isIntegrationMode,
    submittalListLoading,
    submittalListLoadedOnce,
    fetchLogData,
    mutationDeleteSubmittal,
    setOnDataUpdateCallback
  };
}

export default useFeatureSubmittalLogData;
